<script setup lang="ts">
import { useFormContainer } from "~/composables/useFormContainer";
import FormCard from "~/components/ui/FormCard.vue";
import PromoBox from "~/components/ui/PromoBox.vue";
import type { IRate } from "~/interfaces";
import { getPromoUpgradeModel } from "~/utils/promo";
import BaseForm from "~/components/ui/BaseForm.vue";
import LoadingSpinner from "~/components/ui/LoadingSpinner.vue";
import Perks from "~/components/static/Perks.vue";

const props = defineProps({
  progress: { type: Number, required: true },
  title: { type: String, required: true },
  ctaText: { type: String, default: "CONTINUA" },
  hasDetails: { type: Boolean, required: true },
  hasInsurance: { type: Boolean, required: true },
  hasService: { type: Boolean, required: true },
  detailsOpen: { type: Boolean, default: false },
  hasLoadingSpinner: { type: Boolean, default: false },
  titleLoading: { type: String, default: '' },
  textLoading: { type: String, default: '' }
});

const { FormContainer } = useFormContainer();

const emit = defineEmits(["init", "submit", "decline"]);

const loading = ref(false);
const sendingServerRequest = useState("sendingServerRequest", () => false );
const promoUpgraded = useState("promoUpgraded", () => null );
const promoBoxLoading = computed(() => sendingServerRequest.value && promoUpgraded.value !== false);
const upgradeModel = getPromoUpgradeModel(props.hasService)!;
const model = getPromoModel()!;

const { locale } = await useLocale("forms", "promo", true);

const detailsHtml = computed(() => {
  let html = '';
  if (props.hasInsurance && props.hasService) {
    html = locale.value?.details_when_has_insurance_and_service
      .replace("%insurance", upgradeModel.insurance?.toString() ?? "") ?? '';
  } else if (props.hasInsurance) {
    html = locale.value?.details_when_has_only_insurance
      .replace("%insurance", upgradeModel.insurance?.toString() ?? "") ?? '';
  } else if (props.hasService) {
    html = locale.value?.details_when_has_only_service ?? '';
  }
  if (props.hasInsurance) {
    html += locale.value?.details_insurance_asterisk ?? '';
  }
  return html;
});

const submit = (model: IRate) => {
  loading.value = true;
  emit("submit", model);
  setTimeout(() => {
    loading.value = false;
  });
};
const loadingToshow = ref(true);

onMounted(() => {
  emit("init")
  setTimeout(() => {
    loadingToshow.value = false;
  }, 4000);
});

watch(sendingServerRequest, (val) => {
  if (props.hasLoadingSpinner) loading.value = val
});
</script>

<template>

  <template v-if="loadingToshow">
    <BaseForm>    
      <template #upper>
      <div
        class="mx-auto lg:container text-sm sm:text-base leading-tight text-gray-700 text-center pb-48 pt-36"
      >
        <div class="w-full text-center">
          <div class="spinner mx-auto"></div>
        </div>
          <h1 class="text-lg font-semibold mt-6">{{ titleLoading }}</h1>
          <span v-if="textLoading != null" v-html="textLoading"></span>
          <slot v-if="textLoading == null" name="textLoading" />
        </div>    
      </template>

    </BaseForm>
  </template>

  <template v-else> 
    <FormContainer :progress="progress" class="relative">
      <FormCard :title="title" :has-submit="false">
        <div class="w-full mb-6">
          <p class="pageSubtitle">{{ locale?.text }}</p>
          <div class="mt-14">
            <PromoBox
              :model="upgradeModel"
              :has-details="hasDetails"
              :open-details="detailsOpen"
              :loading="promoBoxLoading"
              :cta="ctaText"
              data-testid="promo"
              @click="submit(upgradeModel)"
            >
              <template #details>
                <span v-html="detailsHtml"></span>
              </template>
            </PromoBox>
          </div>
          <div class="mt-16 text-xs text-center text-gray-700">
            {{ locale?.small_info }}
          </div>
          <div class="text-center mt-8">
            <button
              class="underline text-sm font-semibold md:text-base"
              data-testid="decline"
              @click="emit('decline', model)"
            >
              <LoadingSpinner v-if="loading && promoUpgraded === false"></LoadingSpinner>
              <span v-else >
                {{ locale?.or_confirm_previous_choice }}
              </span>
            </button>
          </div>
        </div>
      </FormCard>
    </FormContainer>
  </template>

  <Perks />
</template>

<style scoped>
div :deep(.form-body) {
  @apply p-0 pb-6 sm:px-6 md:px-8;
}
div :deep(.form-footer) {
  display: none !important;
}
</style>
